import { DateTime } from "luxon";
import axiosClient from "../common/axios/axiosConfig";
import { incrementOnDate, transformDateToType } from "../utils/date-util/dateUtils";
import { showToast } from "../utils/showToast";

export interface IProfessionalSchedulesAvailable {
	convenio: string;
	procedimento: {
		descricao: string;
		_id: string;
	};
	idHorario: string;
	horarioInicial: string;
	telemedicina: boolean;
	valor: number;
}

export interface IProfessional {
	nomeProfissional: string;
	codigoProfissional: string;
	imagemProfissional: string;
	conselho: string;
	numeroConselho: string;
	ufConselho: string;
	telemedicina: boolean;
	nomeConsultorio: string;
	telefoneConsultorio: string;
	codigoIbge: number;
	cep: string;
	logradouro: string;
	numero: string;
	complemento: string;
	bairro: string;
	cidade: string;
	uf: string;
	especialidade: string;
	procedimentos: Array<{
		_id: string;
		description: string;
	}>;
	subEspecialidades: string[];
	biografia: string;
	score: number;
	dataDisponivel: string;
	horariosDisponiveis: IProfessionalSchedulesAvailable[];
	avaliacao: number;
}

export type TipoProfissional = "clinico_geral" | "nutricionista" | "personal_trainer" | "psicologo";

export interface GetProfessionalsParams {
	specialty?: string;
	tipoAtendimento?: "TELEATENDIMENTO" | "PRESENCIAL" | "AMBOS";
	dataInicial?: string;
	dataFinal?: string;
	type?: string;
	price?: number;
	rating?: string;
	search?: string;
	tipoProfissional?: TipoProfissional;
	procedureIds?: string;
	insuranceName?: string;
}

interface GetprofessionalsResponse {
	tamanho: number;
	limite: number;
	deslocamento: number;
	profissionais: IProfessional[];
}

export const getProfessionals = (offset: number, limit: number, params?: GetProfessionalsParams): Promise<GetprofessionalsResponse> => {
	const queryParams = { ...params, offset, limit };

	const today = transformDateToType(new Date());
	queryParams.dataInicial = today.toISODate();

	if (!queryParams.dataFinal) {
		const finalDate = incrementOnDate(30, 'days', today);
		queryParams.dataFinal = finalDate.toISODate();
	}

	const finalDate = DateTime.fromFormat(queryParams.dataFinal, "yyyy-MM-dd");
	queryParams.dataFinal = incrementOnDate(1, 'day', finalDate).toISODate();

	const insurance = window.sessionStorage.getItem("insuranceName") ?? '';
	if (insurance) {
		queryParams.insuranceName = insurance;
	}

	return axiosClient
		.get<GetprofessionalsResponse>("/listar-profissionais", { params: queryParams })
		.then(({ data }) => {
			const formattedProfessionals: IProfessional[] = data.profissionais.map(handleNextAvailableTimes);

			return { ...data, profissionais: formattedProfessionals };
		})
		.catch(() => {
			showToast("error", {
				text: "Erro ao buscar profissionais",
			});
			return { profissionais: [] as IProfessional[] } as GetprofessionalsResponse;
		});
};

const handleNextAvailableTimes = (professional: IProfessional) => {
	const nextAvailableDate = transformDateToType(professional.horariosDisponiveis[0].horarioInicial);

	// Filtrar apenas por horários do dia mais próximo e ordenar pelos horários mais próximos
	const availableTimes = professional.horariosDisponiveis
		.filter(time => {
			const timeDate = transformDateToType(time.horarioInicial);

			return nextAvailableDate.day === timeDate.day && nextAvailableDate.month === timeDate.month;
		})
		.sort((a, b) => {
			const dateA = transformDateToType(a.horarioInicial);
			const dateB = transformDateToType(b.horarioInicial);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;
			return 0;
		});

	return { ...professional, horariosDisponiveis: availableTimes };
};
